/**
 * Timeout warning modal component.
 * Shows countdown and allows user to extend session or logout.
 */

import { getState, subscribe } from '../../state/store.js';
import { clearSession } from '../../state/session.js';
import { extendSession, setCountdownCallback } from '../../utils/idle-timer.js';

/**
 * Format seconds as MM:SS or just seconds.
 * @param {number} seconds - Seconds to format
 * @returns {string} Formatted time
 */
function formatTime(seconds) {
  if (seconds >= 60) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  return `${seconds}`;
}

/**
 * Update the countdown display.
 * @param {number} seconds - Seconds remaining
 */
function updateCountdown(seconds) {
  const countdownEl = document.getElementById('timeout-countdown');
  if (countdownEl) {
    countdownEl.textContent = formatTime(seconds);

    // Add urgency styling when under 30 seconds
    if (seconds <= 30) {
      countdownEl.classList.add('text-red-600', 'font-bold');
    } else {
      countdownEl.classList.remove('text-red-600', 'font-bold');
    }
  }
}

/**
 * Handle "Stay Logged In" button click.
 */
function handleExtend() {
  extendSession();
}

/**
 * Handle "Logout" button click.
 */
function handleLogout() {
  clearSession();
}

/**
 * Update modal visibility based on state.
 */
function updateModalVisibility() {
  const modal = document.getElementById('timeout-modal');
  const { ui } = getState();

  if (ui.showTimeoutWarning) {
    modal.classList.remove('hidden');
    // Focus the extend button for accessibility
    const extendBtn = document.getElementById('extend-session');
    if (extendBtn) {
      setTimeout(() => extendBtn.focus(), 100);
    }
  } else {
    modal.classList.add('hidden');
  }
}

/**
 * Initialize the timeout warning modal.
 * Sets up event handlers and state subscription.
 * @returns {Function} Cleanup function
 */
export function initTimeoutWarning() {
  // Set up countdown callback
  setCountdownCallback(updateCountdown);

  // Attach button handlers
  const extendBtn = document.getElementById('extend-session');
  const logoutBtn = document.getElementById('logout-now');

  if (extendBtn) {
    extendBtn.addEventListener('click', handleExtend);
  }

  if (logoutBtn) {
    logoutBtn.addEventListener('click', handleLogout);
  }

  // Subscribe to state changes
  const unsubscribe = subscribe((state, path) => {
    if (path.includes('ui.showTimeoutWarning') || path === '*') {
      updateModalVisibility();
    }
  });

  // Initial visibility check
  updateModalVisibility();

  // Return cleanup function
  return () => {
    unsubscribe();
    if (extendBtn) {
      extendBtn.removeEventListener('click', handleExtend);
    }
    if (logoutBtn) {
      logoutBtn.removeEventListener('click', handleLogout);
    }
    setCountdownCallback(null);
  };
}
